import { EmailsService } from './../../services/emails.service';
import { IssuerRequestsService } from './../../services/issuer-requests.service';
import { Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { DcAlertService } from 'src/app/ui-kit/dc-alert/dc-alert.service';
import { ConnectionService } from '../../services/connection.service';
import { TransferService } from '../../services/transfer.service';
import { Router } from '@angular/router';
import { PlatformLocation } from '@angular/common';
import { FileManagementService } from '../../services/file-management.service';
import * as uuid from 'uuid';
import { KydRequestService } from '../../services/kyd-request.service';
import { DistributorsService } from '../../services/distributors.service';
import { Distributor } from '../../model/distributor';

interface FileRef {
  title: string;
  contentType: string;
  distributorID: string;
  fileName: string;
  issuerID: string[];
  requestID?: string[];
  isForAll: boolean;
  id: string;
  fileExt?: string;
  createdOn?: Date;
  updatedOn?: Date;
  userTags?: string[];
  partition?: number;
}

interface DistributorInfo {
  distributorID: string;
  distributorName?: string;
}

interface DropdownItem {
  id: string;
  displayValue: string;
}

@Component({
    selector: 'deltacapita-upload-file',
    templateUrl: './upload-file.component.html',
    styleUrls: ['./upload-file.component.scss'],
    standalone: false
})
export class UploadFileComponent implements OnInit {
  // UploadFileComponent used in two places hence double-declared in both places
  @Input() modal: NgbModalRef;
  @ViewChild('loadingSpinner', { static: true }) loadingSpinner: TemplateRef<any>;
  loading: boolean = true;
  distributorsDropdownData: DropdownItem[];

  uploadFileForm: UntypedFormGroup;
  fileRef: FileRef;
  currentRow;
  distributorInfo: DistributorInfo;
  requesters: any[];
  isForAllChecked: boolean;
  availableTypes: string[];

  constructor(
    private fb: UntypedFormBuilder,
    private connectionService: ConnectionService,
    private dcAlertService: DcAlertService,
    private transferService: TransferService,
    private fileManagementService: FileManagementService,
    private kydRequestService: KydRequestService,
    private distributorsService: DistributorsService,
    private issuerRequestsService: IssuerRequestsService,
    private emailsService: EmailsService,
    private router: Router,
    location: PlatformLocation
  ) {
    location.onPopState((event) => {
      // ensure that modal is opened
      if (this.modal !== undefined) {
        this.modal.close();
      }
    });
  }

  base64: any;
  MAX_IMAGE_SIZE = 100663296;
  fileName: string;
  fileType: string;

  ngOnInit(): void {
    this.currentRow = this.transferService.getData();
    this.transferService.clearData();

    this.distributorsService.getAllDistributors().subscribe((distributors: Distributor[]) => {
      this.distributorsDropdownData = this.makeDropdownItems(distributors, 'id', 'name');
      this.loading = false;
    });

    this.distributorInfo = {
      distributorID: this.currentRow.id,
      distributorName: this.currentRow.name,
    };
    // console.log(this.currentRow, this.distributorInfo);

    this.connectionService.getRequestersForDistributor(this.distributorInfo.distributorID).subscribe(
      (data) => {
        this.requesters = data;
        // console.log(this.requesters, 'requesters');
        this.loading = false;
      },
      (error) => {
        console.log(error);
      }
    );

    this.kydRequestService.getRequestTypesByDistributorId(this.distributorInfo.distributorID).subscribe(
      (data) => {
        this.availableTypes = data;
        // console.log(this.availableTypes, 'types');
        this.loading = false;
      },
      (error) => {
        console.log(error);
      }
    );

    this.initializeForm();

    this.uploadFileForm.valueChanges.subscribe((values) => {
      if (values.forAll) {
        this.isForAllChecked = true;
      } else {
        this.isForAllChecked = false;
      }

      if (values.distributor) {
        this.distributorInfo = { distributorID: values.distributor };
        this.connectionService.getRequestersForDistributor(this.distributorInfo.distributorID).subscribe(
          (data) => {
            this.requesters = data;
          },
          (error) => {
            console.log(error);
          }
        );

        this.kydRequestService.getRequestTypesByDistributorId(this.distributorInfo.distributorID).subscribe(
          (data) => {
            this.availableTypes = data;
          },
          (error) => {
            console.log(error);
          }
        );
      }
    });
  }

  initializeForm(): void {
    this.uploadFileForm = this.fb.group({
      distributor: new UntypedFormControl(null, !this.currentRow!.id ? Validators.required : null),
      title: new UntypedFormControl(null, Validators.required),
      userTag: new UntypedFormControl(null),
      forAll: new UntypedFormControl(false),
      // The formArray, empty
      issuers: this.fb.array([]),
      types: this.fb.array([]),
      emailCheckbox: new UntypedFormControl(true),
    });
  }

  onIssuerCheckboxChange(e) {
    const issuer: UntypedFormArray = this.uploadFileForm.get('issuers') as UntypedFormArray;

    if (e.target.checked && !issuer.value.includes(e.target.value)) {
      issuer.push(new UntypedFormControl(e.target.value));
    } else {
      const index = issuer.controls.findIndex((x) => x.value === e.target.value);

      issuer.removeAt(index);
    }
    // console.log(issuer, 'issuer form control');
  }

  // each type of request has unique request id
  onRequestTypesCheckboxChange(e) {
    const type: UntypedFormArray = this.uploadFileForm.get('types') as UntypedFormArray;

    if (e.target.checked && !type.value.includes(e.target.value)) {
      type.push(new UntypedFormControl(e.target.value));
    } else {
      const index = type.controls.findIndex((x) => x.value === e.target.value);

      type.removeAt(index);
    }
    // console.log(type, 'request type form control');
  }

  async onFileSelected(event) {
    const files = event.target.files;

    if (!files.length) {
      return alert('Please choose a file to upload first.');
    }
    const file = files[0];

    if (file.size > this.MAX_IMAGE_SIZE) {
      this.dcAlertService.showAlert({
        type: 'danger',
        message: `File size exceeded maximum allowed payload size (${Math.round(
          this.MAX_IMAGE_SIZE / 1024 / 1024
        )} MB)`,
      });
      return;
    }

    this.fileName = file.name;
    this.fileType = file.type;

    this.base64 = await this.fileManagementService.getBase64(file);
  }

  async uploadFile(): Promise<void> {
    const { title, forAll, issuers, userTag, types, emailCheckbox } = this.uploadFileForm.value;

    const id: string = uuid.v4();
    const userTagWithDate: string = userTag ? `${new Date().toLocaleDateString()}: ${userTag}` : null;
    this.fileRef = {
      title: title,
      contentType: this.fileType,
      distributorID: this.distributorInfo.distributorID,
      fileName: this.fileName,
      issuerID: issuers,
      requestID: types,
      isForAll: forAll,
      id: id,
      fileExt: this.fileName.split('.').pop().toUpperCase(),
      createdOn: new Date(),
      userTags: [userTagWithDate],
    };

    // console.log(this.fileRef);

    const partition: number = this.base64.length > 1000000 ? Math.ceil(this.base64.length / 1000000) : 0;
    this.loading = true;
    if (partition) {
      this.fileRef.partition = partition;

      for (let i = 0; i < partition; i++) {
        await this.fileManagementService
          .uploadFilePartition(
            this.fileName,
            this.base64.slice(i * 1000000, (i + 1) * 1000000),
            this.fileType,
            id,
            i + 1
          )
          .toPromise()
          .then(() => {
            console.log(`partition ${i + 1} uploaded ...`);
          })
          .catch((e) => {
            this.loading = false;
            console.log(e);
            this.dcAlertService.showAlert({
              type: 'danger',
              message: `Error uploading ${this.fileName}!`,
            });
          });
      }
      await this.fileManagementService
        .updateRef(this.fileRef, id)
        .toPromise()
        .then(async () => {
          await this.sendNewDocNotificationEmails(emailCheckbox, types, forAll, issuers);
          this.loading = false;
          this.dcAlertService.showAlert({
            type: 'success',
            message: `Successfully uploaded ${this.fileName}`,
          });
        })
        .catch((e) => {
          console.log(e);
          this.loading = false;
          this.dcAlertService.showAlert({
            type: 'danger',
            message: `Error uploading ${this.fileName}!`,
          });
        });
    } else {
      await this.fileManagementService
        .uploadFile(this.fileName, this.base64, this.fileType, id)
        .toPromise()
        .then(async (res) => {
          return await this.fileManagementService.uploadRef(this.fileRef, id);
        })
        .then(async () => {
          await this.sendNewDocNotificationEmails(emailCheckbox, types, forAll, issuers);
          this.loading = false;
          this.dcAlertService.showAlert({
            type: 'success',
            message: `Successfully uploaded ${this.fileName}`,
          });
        })
        .catch((e) => {
          this.loading = false;
          console.log(e);
          this.dcAlertService.showAlert({
            type: 'danger',
            message: `Error uploading ${this.fileName}!`,
          });
        });
    }

    this.transferService.notifyRefresh();

    this.modal.dismiss();
  }

  makeDropdownItems(data, idField, displayValueField): DropdownItem[] {
    return data.map((dataItem) => {
      return {
        id: dataItem[idField],
        displayValue: Array.isArray(displayValueField)
          ? `${dataItem[displayValueField[0]]} (${dataItem[displayValueField[1]]})`
          : dataItem[displayValueField],
      };
    });
  }

  async sendNewDocNotificationEmails(emailCheckbox: boolean, types: string[], forAll: boolean, issuers: string[]) {
    if (emailCheckbox) {
      let mailList: any[] = [];
      for (const requestID of types) {
        mailList.push(
          ...(await this.issuerRequestsService.getActiveIssContactsByRequestIdFromIssRequestsPromise(requestID)).filter(
            (x) => {
              return forAll ? true : issuers.includes(x.issuerid);
            }
          )
        );
      }

      mailList.forEach(async (x) => {
        // await this.emailsService.sendEmailsWithoutTemplate(
        //   x.email,
        //   'New Document',
        //   `Dear ${x.name}, A new document for ${this.distributorInfo.distributorName} has been produced, please log into our platform to review.`
        // );

        // mail with template
        await this.emailsService.sendEmailsToIssuerContactWithDistributorInfo(
          x.email,
          'NewDocumentUploadedByDC',
          x.distributorId,
          {
            requestId: x.requestId,
            requestType: x.type,
            formTemplate: x.template || x.type,
          }
        );
      });
    }
  }
}
